<script>
import { required } from "vuelidate/lib/validators";
import validationMessages from "@/components/validations";
import Football from "@/services/Football";
import Swal from "sweetalert2";

export default {
  components: { validationMessages },

  props: {
    stageOptions: {
      type: Array,
      default: () => [],
    },
    teamOptions: {
      type: Array,
      default: () => [],
    },
    matchOptions: {
      type: Array,
      default: () => [],
    },
    standingOptions: {
      type: Array,
      default: () => [],
    },
    provider: {
      type: String,
      default: "Azerion",
    },
  },

  data() {
    return {
      stage_id: "",
      teams: [
        {
          team_id: "",
          rank: "",
          points: "",
          matches_played: "",
          matches_won: "",
          matches_drawn: "",
          matches_lost: "",
          goals_for: "",
          goals_against: "",
          goal_difference: "",
        },
      ],
      submitted: false,
      tryingToSubmit: false,
    };
  },

  computed: {
    selectedStage() {
      return (this.stageOptions || []).find((stage) => stage.stage_id === this.stage_id) || null;
    },
    stageTeamOptions() {
      if (!this.selectedStage) {
        return [];
      }

      const tournamentId = this.selectedStage.tournament_id;
      const stageId = this.selectedStage.stage_id;
      const teamMap = {};

      (this.matchOptions || []).forEach((match) => {
        const sameStage = match.stage_id && match.stage_id === stageId;
        const sameTournament = tournamentId && match.tournament_id === tournamentId;
        if (!sameStage && !sameTournament) {
          return;
        }
        if (match.home_team_id) {
          teamMap[match.home_team_id] = {
            team_id: match.home_team_id,
            team_name: match.home_team_name || match.home_team_id,
            team_code: match.home_team_code || "",
          };
        }
        if (match.away_team_id) {
          teamMap[match.away_team_id] = {
            team_id: match.away_team_id,
            team_name: match.away_team_name || match.away_team_id,
            team_code: match.away_team_code || "",
          };
        }
      });

      (this.standingOptions || []).forEach((row) => {
        if (!tournamentId || row.tournament_id !== tournamentId || !row.team_id) {
          return;
        }
        teamMap[row.team_id] = {
          team_id: row.team_id,
          team_name: row.team_name || row.team_id,
          team_code: row.team_code || "",
        };
      });

      (this.teamOptions || []).forEach((team) => {
        if (!team.team_id || !teamMap[team.team_id]) {
          return;
        }
        teamMap[team.team_id] = Object.assign({}, teamMap[team.team_id], team);
      });

      // First standings for a new stage/tournament: allow picking from all teams.
      const teams = Object.values(teamMap);
      if (!teams.length) {
        return this.teamOptions || [];
      }

      return teams.sort((a, b) =>
        String(a.team_name || "").localeCompare(String(b.team_name || ""))
      );
    },
  },

  watch: {
    stage_id() {
      this.teams.forEach((row) => {
        row.team_id = "";
      });
    },
  },

  validations: {
    stage_id: { required },
  },

  methods: {
    closeModal() {
      this.$bvModal.hide("add_sport_standings");
    },

    formatError(error) {
      if (!error) return "Failed to save standings";
      if (Array.isArray(error)) return error.join(", ");
      return error;
    },

    addTeamRow() {
      this.teams.push({
        team_id: "",
        rank: "",
        points: "",
        matches_played: "",
        matches_won: "",
        matches_drawn: "",
        matches_lost: "",
        goals_for: "",
        goals_against: "",
        goal_difference: "",
      });
    },

    removeTeamRow(index) {
      if (this.teams.length === 1) return;
      this.teams.splice(index, 1);
    },

    buildTeamPayload(row) {
      const payload = { team_id: row.team_id };
      const numericFields = [
        "rank",
        "points",
        "matches_played",
        "matches_won",
        "matches_drawn",
        "matches_lost",
        "goals_for",
        "goals_against",
      ];

      numericFields.forEach((field) => {
        if (row[field] !== "" && row[field] != null) {
          payload[field] = Number(row[field]);
        }
      });

      if (row.goal_difference !== "" && row.goal_difference != null) {
        payload.goal_difference = String(row.goal_difference);
      }

      return payload;
    },

    async createSportStandings() {
      this.tryingToSubmit = true;
      this.submitted = true;
      this.$v.$touch();

      if (this.$v.$invalid) {
        this.tryingToSubmit = false;
        return;
      }

      const validTeams = this.teams
        .filter((row) => row.team_id)
        .map((row) => this.buildTeamPayload(row));

      if (!validTeams.length) {
        Swal.fire("Fail!", "Add at least one team with a team selected", "warning");
        this.tryingToSubmit = false;
        return;
      }

      const payload = {
        stage_id: this.stage_id,
        provider: this.provider,
        teams: validTeams,
      };

      try {
        await Football.createSportStandings(payload);
        Swal.fire("Success!", "Standings saved successfully!", "success");
        this.closeModal();
        this.$emit("onRefresh");
      } catch (error) {
        const err = error.response && error.response.data && error.response.data.error
          ? error.response.data.error
          : "";
        Swal.fire("Fail!", this.formatError(err), "warning");
      } finally {
        this.tryingToSubmit = false;
      }
    },

    resetForm() {
      this.stage_id = "";
      this.teams = [
        {
          team_id: "",
          rank: "",
          points: "",
          matches_played: "",
          matches_won: "",
          matches_drawn: "",
          matches_lost: "",
          goals_for: "",
          goals_against: "",
          goal_difference: "",
        },
      ];
      this.submitted = false;
      this.$v.$reset();
    },
  },
};
</script>

<template>
  <b-modal
    id="add_sport_standings"
    title="Add Standings"
    title-class="font-18"
    centered
    size="xl"
    @hidden="resetForm"
  >
    <form @submit.prevent="createSportStandings">
      <div class="form-group mb-3">
        <label for="standings_stage_id">Stage <span class="text-danger">*</span></label>
        <b-form-select
          id="standings_stage_id"
          v-model="stage_id"
          :class="{ 'is-invalid': submitted && $v.stage_id.$error }"
        >
          <option value="">Select stage</option>
          <option
            v-for="stage in stageOptions"
            :key="stage.stage_id"
            :value="stage.stage_id"
          >
            {{ stage.stage_name }} ({{ stage.tournament_name || stage.tournament_id }})
          </option>
        </b-form-select>
        <validationMessages v-if="submitted" :field="$v.stage_id" />
      </div>

      <div
        v-for="(row, index) in teams"
        :key="'standing-row-' + index"
        class="border rounded p-3 mb-3"
      >
        <div class="d-flex justify-content-between align-items-center mb-2">
          <h6 class="m-0">Team {{ index + 1 }}</h6>
          <a
            v-if="teams.length > 1"
            href="javascript:void(0);"
            class="text-danger"
            title="Remove row"
            @click="removeTeamRow(index)"
          >
            <i class="uil uil-trash-alt font-size-18"></i>
          </a>
        </div>

        <div class="row">
          <div class="col-md-6">
            <div class="form-group mb-2">
              <label>Team</label>
              <b-form-select v-model="row.team_id" :disabled="!stage_id">
                <option value="">{{ stage_id ? "Select team" : "Select stage first" }}</option>
                <option
                  v-for="team in stageTeamOptions"
                  :key="'standing-' + index + '-' + team.team_id"
                  :value="team.team_id"
                >
                  {{ team.team_name }}
                </option>
              </b-form-select>
            </div>
          </div>
          <div class="col-md-3">
            <div class="form-group mb-2">
              <label>Rank</label>
              <input v-model="row.rank" type="number" min="1" class="form-control" />
            </div>
          </div>
          <div class="col-md-3">
            <div class="form-group mb-2">
              <label>Points</label>
              <input v-model="row.points" type="number" min="0" class="form-control" />
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-md-2">
            <div class="form-group mb-0">
              <label title="Matches Played">PL <span class="text-muted fw-normal">(Played)</span></label>
              <input v-model="row.matches_played" type="number" min="0" class="form-control form-control-sm" />
            </div>
          </div>
          <div class="col-md-2">
            <div class="form-group mb-0">
              <label title="Matches Won">W <span class="text-muted fw-normal">(Won)</span></label>
              <input v-model="row.matches_won" type="number" min="0" class="form-control form-control-sm" />
            </div>
          </div>
          <div class="col-md-2">
            <div class="form-group mb-0">
              <label title="Matches Drawn">D <span class="text-muted fw-normal">(Drawn)</span></label>
              <input v-model="row.matches_drawn" type="number" min="0" class="form-control form-control-sm" />
            </div>
          </div>
          <div class="col-md-2">
            <div class="form-group mb-0">
              <label title="Matches Lost">L <span class="text-muted fw-normal">(Lost)</span></label>
              <input v-model="row.matches_lost" type="number" min="0" class="form-control form-control-sm" />
            </div>
          </div>
          <div class="col-md-2">
            <div class="form-group mb-0">
              <label title="Goals For">GF <span class="text-muted fw-normal">(Goals For)</span></label>
              <input v-model="row.goals_for" type="number" min="0" class="form-control form-control-sm" />
            </div>
          </div>
          <div class="col-md-2">
            <div class="form-group mb-0">
              <label title="Goals Against">GA <span class="text-muted fw-normal">(Goals Against)</span></label>
              <input v-model="row.goals_against" type="number" min="0" class="form-control form-control-sm" />
            </div>
          </div>
        </div>
      </div>

      <a
        href="javascript:void(0);"
        class="btn btn-primary waves-effect waves-light btn-sm"
        @click="addTeamRow"
      >
        <i class="mdi mdi-plus me-2"></i>
        Add Team Row
      </a>
    </form>

    <template v-slot:modal-footer>
      <b-button variant="light" @click="closeModal">Close</b-button>
      <b-button variant="primary" @click="createSportStandings" :disabled="tryingToSubmit">
        <b-spinner v-show="tryingToSubmit" small></b-spinner>
        Save
      </b-button>
    </template>
  </b-modal>
</template>
